<template>
      <el-dialog
      v-model="dialogVisible"
      :width="currentDialogWidth"
      :close-on-click-modal="false"
      :close-on-press-escape="true"
      class="contact-modal-dialog"
      @close="handleClose"
    >
    
    <div class="modal-intro">
      <h1>Let's Connect</h1>
      <p>Have a question or comment? Use the form below to send us a message</p>
      <div class="triangle-down"></div>
    </div>
    
    <el-form
      ref="formRef"
      :model="formData"
      :rules="formRules"
      label-position="top"
      class="contact-form"
      @submit.prevent="submitForm"
      :hide-required-asterisk="true"
    >
      <el-row :gutter="24">
        <!-- 左侧：姓名、电话、邮箱 -->
        <el-col :xs="24" :lg="12">
          <el-form-item label="Your Name" prop="name">
            <el-input
              v-model="formData.name"
              placeholder="Enter your name"
              size="large"
              clearable
            />
          </el-form-item>
          
          <el-form-item label="Your Phone" prop="phone">
            <el-input
              v-model="formData.phone"
              placeholder="Enter your phone number"
              size="large"
              clearable
            />
          </el-form-item>
          
          <el-form-item label="Your Email" prop="email">
            <el-input
              v-model="formData.email"
              placeholder="Enter your email address"
              size="large"
              clearable
            />
          </el-form-item>
        </el-col>
        
        <!-- 右侧：留言 -->
        <el-col :xs="24" :lg="12">
          <el-form-item label="Your Comment" prop="comment">
            <el-input
              v-model="formData.comment"
              type="textarea"
              :rows="11"
              placeholder="Enter your message or comment"
              size="large"
              resize="none"
              class="comment-textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
      
      <div class="form-info">
        <p class="info-item">
          <el-icon class="asterisk"><Warning /></el-icon>
          We'd love to hear from you - please use the form to send us your message or ideas.
        </p>
        <p class="info-item">
          <el-icon class="asterisk"><Warning /></el-icon>
          Email: mark.zhi@yedao.org
        </p>
      </div>
      
      <el-form-item>
        <el-button
          type="primary"
          size="large"
          :loading="isSubmitting"
          class="submit-btn"
          @click="submitForm"
        >
          {{ isSubmitting ? 'Submitting...' : 'SUBMIT CONTACT' }}
        </el-button>
      </el-form-item>
    </el-form>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch, defineProps, defineEmits, onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Warning } from '@element-plus/icons-vue'
import { getApi } from '@/axios'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'submit'])

const formRef = ref()
const isSubmitting = ref(false)

const formData = reactive({
  name: '',
  phone: '',
  email: '',
  comment: ''
})

// 表单验证规则 - 调整为国外用户友好的验证
const formRules = {
  name: [
    { required: true, message: 'Please enter your name', trigger: 'blur' },
    { min: 1, max: 50, message: 'Name should be between 1 and 50 characters', trigger: 'blur' }
  ],
  phone: [
    { required: true, message: 'Please enter your phone number', trigger: 'blur' },
    { min: 7, max: 20, message: 'Phone number should be between 5 and 20 digits', trigger: 'blur' }
  ],
  email: [
    { required: true, message: 'Please enter your email address', trigger: 'blur' },
    { type: 'email', message: 'Please enter a valid email address', trigger: 'blur' }
  ],
  comment: [
    { required: true, message: 'Please enter your message', trigger: 'blur' },
    { min: 5, max: 1000, message: 'Message should be between 5 and 1000 characters', trigger: 'blur' }
  ]
}

// 响应式对话框宽度
const currentDialogWidth = ref(window.innerWidth <= 768 ? '95%' : '600px')

// 监听窗口大小变化
const updateDialogWidth = () => {
  currentDialogWidth.value = window.innerWidth <= 768 ? '95%' : '600px'
}

// 组件挂载时添加监听器
onMounted(() => {
  window.addEventListener('resize', updateDialogWidth)
})

// 组件卸载时移除监听器
onUnmounted(() => {
  window.removeEventListener('resize', updateDialogWidth)
})

// 对话框显示状态
const dialogVisible = computed({
  get: () => props.visible,
  set: (value) => {
    if (!value) {
      emit('close')
    }
  }
})

// 监听 visible 变化，重置表单
watch(() => props.visible, (newVal) => {
  if (newVal) {
    resetForm()
  }
})

const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields()
  }
  Object.assign(formData, {
    name: '',
    phone: '',
    email: '',
    comment: ''
  })
}

const handleClose = () => {
  emit('close')
}

const submitForm = async () => {
  if (isSubmitting.value) return
  
  try {
    // 表单验证
    await formRef.value.validate()
    
    isSubmitting.value = true
    
    // 这里可以添加实际的表单提交逻辑
    console.log('Form data submitted:', formData)
    
    const res = await getApi('https://script.google.com/macros/s/AKfycbxaNbn2EMPskITQz6rw0Rwyk1jZyinjhJneH2lf6rafwW_C0NW0OBobFCb0ga2PqAhPfg/exec', formData)
    console.log(res)    
    // 提交成功
    ElMessage.success('Message sent successfully!')
    emit('submit', { ...formData })
    
    // 重置表单并关闭弹窗
    resetForm()
    emit('close')
    
  } catch (error) {
    if (error.message) {
      ElMessage.error('Please check your input and try again')
    } else {
      ElMessage.error('Submission failed, please try again later')
      console.error('Submission failed:', error)
    }
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style lang="scss" scoped>
.contact-modal-dialog {
  :deep(.el-dialog) {
    border-radius: 12px;
    overflow: hidden;
    
    .el-dialog__header {
      padding: 24px 24px 16px;
      border-bottom: 1px solid #f0f0f0;
      margin: 0;
      
      .el-dialog__title {
        font-size: 28px;
        font-weight: 700;
        color: #000;
      }
      
      .el-dialog__headerbtn {
        top: 24px;
        right: 24px;
        
        .el-dialog__close {
          font-size: 20px;
          color: #666;
          
          &:hover {
            color: #000;
          }
        }
      }
    }
    
    .el-dialog__body {
      padding: 0;
    }
  }
}

.modal-intro {
  padding: 16px 24px 24px;
  text-align: left;
  
  p {
    margin: 0 0 12px 0;
    color: #000;
    font-size: 16px;
    line-height: 1.5;
  }
}

.triangle-down {
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid #000;
}

.contact-form {
  padding: 0 24px 24px;
  
  :deep(.el-form-item) {
    margin-bottom: 20px;
    
    .el-form-item__label {
      font-weight: 500;
      color: #000;
      font-size: 14px;
      padding-bottom: 8px;
    }
    
    .el-input__wrapper,
    .el-textarea__inner {
      border-radius: 8px;
      border: 1px solid #ddd;
      
      &:hover {
        border-color: #409eff;
      }
      
      &.is-focus {
        border-color: #409eff;
        box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1);
      }
    }
    
    .el-input__inner,
    .el-textarea__inner {
      font-size: 14px;
      
      &::placeholder {
        color: #999;
      }
    }
  }
  
  :deep(.el-row) {
    margin-bottom: 0;
  }
  
  :deep(.el-col) {
    margin-bottom: 0;
  }
  
  // 留言文本框特殊样式
  .comment-textarea {
    :deep(.el-textarea__inner) {
      height: 100%;
      min-height: 200px;
    }
  }
}

.form-info {
  margin-bottom: 24px;
  
  .info-item {
    margin: 0 0 8px 0;
    font-size: 14px;
    color: #666;
    line-height: 1.5;
    display: flex;
    align-items: flex-start;
    
    .asterisk {
      color: #f56c6c;
      margin-right: 8px;
      margin-top: 2px;
      flex-shrink: 0;
    }
  }
}

.submit-btn {
  // width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  background: #409eff;
  border: none;
  
  &:hover {
    background: #337ecc;
    transform: translateY(-1px);
    box-shadow: 0 8px 25px rgba(64, 158, 255, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
}

// 移动端优化
@media (max-width: 768px) {
  .contact-modal-dialog {
    :deep(.el-dialog) {
      margin: 8px;
      
      .el-dialog__header {
        padding: 20px 20px 16px;
        
        .el-dialog__title {
          font-size: 24px;
        }
        
        .el-dialog__headerbtn {
          top: 20px;
          right: 20px;
        }
      }
    }
  }
  
  .modal-intro {
    padding: 16px 20px 20px;
  }
  
  .contact-form {
    padding: 0 20px 20px;
    
    :deep(.el-form-item) {
      margin-bottom: 16px;
    }
    
    // 移动端留言框高度调整
    .comment-textarea {
      :deep(.el-textarea__inner) {
        min-height: 120px;
      }
    }
  }
  
  .submit-btn {
    width: 100%;
    height: 44px;
    font-size: 15px;
  }
}

// 小屏幕设备
@media (max-width: 480px) {
  .contact-modal-dialog {
    :deep(.el-dialog) {
      margin: 6px;
      
      .el-dialog__header {
        padding: 16px 16px 12px;
        
        .el-dialog__title {
          font-size: 22px;
        }
        
        .el-dialog__headerbtn {
          top: 16px;
          right: 16px;
        }
      }
    }
  }
  
  .contact-form {
    padding: 0 16px 16px;
  }
}
</style>
